import { useMemo, useState } from 'react';
import styles from './SkuCompetitionTable.module.css';
import { LineChart, type Series } from '@/components/charts/LineChart';
import { BRAND_COLORS } from '@/hooks/useCompetition';
import { useSkuCompetition } from '@/hooks/useSkuCompetition';
import { useSkuSeries } from '@/hooks/useSkuSeries';
import type { DateRange } from '@/lib/dateRange';

/**
 * Ranked competitor SKUs with a compare-trends action — Figma 4139:74371.
 *
 * Ad SOV% is in the comp but cannot be served: advertising is partner-private
 * per brand, so a competitor's ad share is not ours to show. The column is
 * present and marked rather than silently dropped, so the gap is legible.
 */
export function SkuCompetitionTable({
  customerName,
  category,
  city,
  range,
}: {
  customerName: string;
  category: string | undefined;
  city: string | undefined;
  range: DateRange;
}) {
  const q = useSkuCompetition(customerName, category, city, range, true);
  const [picked, setPicked] = useState<string[]>([]);
  const [comparing, setComparing] = useState(false);

  // Memoised so the series useMemo below is not invalidated on every render.
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const trend = useSkuSeries(customerName, picked, city, range, comparing && picked.length > 0);

  const series: Series[] = useMemo(() => {
    if (!trend.data) return [];
    return picked.map((pid, i) => ({
      key: pid,
      label: rows.find((r) => r.productId === pid)?.sku ?? pid,
      color: BRAND_COLORS[i % BRAND_COLORS.length],
      points: trend.data.byProduct[pid] ?? [],
      format: (v: number) => `${v.toFixed(1)}%`,
    }));
  }, [trend.data, picked, rows]);

  const toggle = (pid: string): void => {
    setPicked((p) => (p.includes(pid) ? p.filter((x) => x !== pid) : [...p, pid]));
  };

  if (!category) {
    return <p className={styles.state}>Pick a category first — SKUs are only comparable within one.</p>;
  }
  if (q.isLoading) return <p className={styles.state}>Loading competitor SKUs…</p>;
  if (q.isError) return <p className={styles.state}>{q.error.message}</p>;

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.title}>
          All SKUs
          <span className={styles.hint}>(Select SKUs to compare trends)</span>
        </span>
        <span className={styles.right}>
          <span className={styles.count}>{picked.length} selected</span>
          <button
            type="button"
            className={styles.compare}
            disabled={picked.length === 0}
            onClick={() => { setComparing((v) => !v); }}
          >
            {comparing ? 'Hide Trends' : 'Compare Trends'}
          </button>
        </span>
      </div>

      {comparing && picked.length > 0 && (
        <div style={{ border: '1px solid #eceef1', borderRadius: 10, padding: 16, marginBottom: 14 }}>
          {trend.isLoading && <p className={styles.state}>Loading trends…</p>}
          {trend.isError && <p className={styles.state}>{trend.error.message}</p>}
          {trend.data && <LineChart labels={trend.data.labels} series={series} yMax={100} height={300} />}
          <p className={styles.note}>Weighted OSA% over the selected window, one line per SKU.</p>
        </div>
      )}

      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.c} style={{ width: 44 }} />
              <th className={styles.c} style={{ width: 60 }}>Rank</th>
              <th className={styles.l}>Brand / SKU</th>
              <th>Category Share</th>
              <th>Weighted OSA%</th>
              <th>Ad SOV%</th>
              <th>MRP</th>
              <th>SP</th>
              <th>Discount %</th>
              <th>PPU</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const on = picked.includes(r.productId);
              return (
                <tr key={r.productId} className={`${styles.row} ${on ? styles.on : r.mine ? styles.mine : ''}`}>
                  <td className={styles.c}>
                    <input
                      type="checkbox"
                      checked={on}
                      aria-label={`Compare ${r.sku}`}
                      onChange={() => { toggle(r.productId); }}
                    />
                  </td>
                  <td className={styles.c}>{String(i + 1).padStart(2, '0')}</td>
                  <td className={styles.l}>
                    <span className={styles.name}>
                      <span className={styles.thumb} />
                      <span className={styles.nameCol}>
                        <span className={styles.nameText}>
                          {r.sku} {r.mine && <span className={styles.you}>(You)</span>}
                        </span>
                        <span className={styles.pills}>
                          <span className={styles.pill}>{r.brand}</span>
                          {r.grammage && <span className={styles.pill}>{r.grammage}</span>}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td>{r.categorySharePct == null ? '—' : `${r.categorySharePct.toFixed(1)}%`}</td>
                  <td>{r.osaPct == null ? '—' : `${r.osaPct.toFixed(1)}%`}</td>
                  <td><span className={styles.na} title="Advertising is partner-private per brand">private</span></td>
                  <td>{r.mrp == null ? '—' : `₹${Math.round(r.mrp).toLocaleString('en-IN')}`}</td>
                  <td>{r.sp == null ? '—' : `₹${Math.round(r.sp).toLocaleString('en-IN')}`}</td>
                  <td>{r.discountPct == null ? '—' : `${r.discountPct.toFixed(1)}%`}</td>
                  <td>{r.ppu == null ? '—' : `₹${r.ppu.toFixed(2)}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.note}>
        Public crawl data. Category share is measured against the whole category, not the rows shown.
        Ad SOV is partner-private per brand and cannot be shown for competitors.
      </p>
    </div>
  );
}
